// src/loaders/loaderUtils.js
import * as THREE from 'three'
import { BufferGeometryUtils } from 'three/examples/jsm/Addons.js'
import { repairMeshTopology } from '../utils/topologyRepair'

/**
 * Standard material configuration used across all loaders
 */
const STANDARD_MATERIAL_CONFIG = {
  side: THREE.DoubleSide,
  metalness: 0,
  roughness: 1,
  flatShading: false,

  vertexColors: true,
  color: 0xffffff,
  transparent: false,

  polygonOffset: true,
  polygonOffsetFactor: -2,
  polygonOffsetUnits: 1.0,
}

/**
 * Standard rotation values used across all loaders
 */
const STANDARD_ROTATION = {
  y: 160, // in degrees
  z: 180, // in degrees
}

/**
 * Validate mesh topology to detect non-manifold geometry, boundaries, and degeneracies
 * @param {THREE.BufferGeometry} geometry - The geometry to validate
 * @param {string} source - Source identifier (e.g., 'OBJ', 'PLY', 'STL')
 * @returns {Object} - Topology validation results
 */
export function validateMeshTopology(geometry, source = 'Unknown') {
  if (!geometry.index) {
    console.warn(`⚠️ [${source}] Cannot validate topology: geometry is not indexed`)
    return null
  }

  const indices = geometry.index.array
  const positions = geometry.attributes.position
  const vertexCount = positions.count
  const triangleCount = indices.length / 3

  // Edge map: key = "v1,v2" (smaller index first), value = array of triangle indices
  const edgeMap = new Map()

  // Build edge map
  for (let i = 0; i < indices.length; i += 3) {
    const v0 = indices[i]
    const v1 = indices[i + 1]
    const v2 = indices[i + 2]
    const triangleIdx = Math.floor(i / 3)

    // Add all three edges of this triangle
    const edges = [
      [v0, v1],
      [v1, v2],
      [v2, v0]
    ]

    for (const [a, b] of edges) {
      // Create canonical edge key (smaller index first)
      const key = a < b ? `${a},${b}` : `${b},${a}`

      if (!edgeMap.has(key)) {
        edgeMap.set(key, [])
      }
      edgeMap.get(key).push(triangleIdx)
    }
  }

  // Analyze edges
  let manifoldEdges = 0
  let boundaryEdges = 0
  let nonManifoldEdges = 0
  const nonManifoldEdgesList = []

  for (const [edge, triangles] of edgeMap.entries()) {
    if (triangles.length === 2) {
      manifoldEdges++
    } else if (triangles.length === 1) {
      boundaryEdges++
    } else {
      nonManifoldEdges++
      if (nonManifoldEdgesList.length < 10) {
        nonManifoldEdgesList.push({ edge, triangleCount: triangles.length })
      }
    }
  }

  // Check for degenerate triangles (zero area)
  let degenerateTriangles = 0
  const v0 = new THREE.Vector3()
  const v1 = new THREE.Vector3()
  const v2 = new THREE.Vector3()
  const edge1 = new THREE.Vector3()
  const edge2 = new THREE.Vector3()

  for (let i = 0; i < indices.length; i += 3) {
    v0.fromBufferAttribute(positions, indices[i])
    v1.fromBufferAttribute(positions, indices[i + 1])
    v2.fromBufferAttribute(positions, indices[i + 2])

    edge1.subVectors(v1, v0)
    edge2.subVectors(v2, v0)

    // Cross product magnitude = 2 * triangle area
    const area = edge1.cross(edge2).length() / 2

    if (area < 1e-10) {
      degenerateTriangles++
    }
  }

  const totalEdges = edgeMap.size
  const isWatertight = boundaryEdges === 0
  const isManifold = nonManifoldEdges === 0 && boundaryEdges === 0
  const quality = ((manifoldEdges / totalEdges) * 100).toFixed(1)

  const results = {
    source,
    vertices: vertexCount,
    triangles: triangleCount,
    totalEdges,
    manifoldEdges,
    boundaryEdges,
    nonManifoldEdges,
    degenerateTriangles,
    isWatertight,
    isManifold,
    quality: parseFloat(quality)
  }

  // Log results
  console.log(`\n🔬 TOPOLOGY VALIDATION [${source}]:`)
  console.log(`├─ Vertices: ${vertexCount.toLocaleString()}`)
  console.log(`├─ Triangles: ${triangleCount.toLocaleString()}`)
  console.log(`├─ Total edges: ${totalEdges.toLocaleString()}`)
  console.log(`├─ Manifold edges: ${manifoldEdges.toLocaleString()} (${quality}%)`)
  console.log(`├─ Boundary edges: ${boundaryEdges.toLocaleString()} ${boundaryEdges > 0 ? '⚠️ (has holes)' : '✅'}`)
  console.log(`├─ Non-manifold edges: ${nonManifoldEdges.toLocaleString()} ${nonManifoldEdges > 0 ? '❌' : '✅'}`)
  console.log(`├─ Degenerate triangles: ${degenerateTriangles.toLocaleString()} ${degenerateTriangles > 0 ? '⚠️' : '✅'}`)
  console.log(`├─ Watertight: ${isWatertight ? '✅ Yes' : '❌ No'}`)
  console.log(`└─ Manifold: ${isManifold ? '✅ Yes' : '❌ No'}\n`)

  if (nonManifoldEdgesList.length > 0) {
    console.log(`⚠️ First ${nonManifoldEdgesList.length} non-manifold edges:`, nonManifoldEdgesList)
  }

  return results
}

/**
 * Get merge tolerance from environment variable or use default
 * @param {number} fallbackTolerance - Fallback tolerance if env var not set
 * @returns {number} - The merge tolerance to use
 */
function getMergeTolerance(fallbackTolerance = 1e-4) {
  const envTolerance = import.meta.env.VITE_MESH_MERGE_TOLERANCE
  if (envTolerance !== undefined) {
    const parsed = parseFloat(envTolerance)
    if (!isNaN(parsed) && parsed > 0) {
      console.log(`📐 Using VITE_MESH_MERGE_TOLERANCE from .env: ${parsed}`)
      return parsed
    }
  }
  return fallbackTolerance
}

/**
 * Check if topology repair should be enabled
 * @returns {boolean} - Whether topology repair is enabled
 */
function isTopologyRepairEnabled() {
  const envRepair = import.meta.env.VITE_MESH_REPAIR_ENABLED
  if (envRepair !== undefined) {
    const isEnabled = envRepair === 'true' || envRepair === true
    console.log(`🔧 Topology repair: ${isEnabled ? '✅ ENABLED' : '⏸️ DISABLED'}`)
    return isEnabled
  }
  return true // Default to enabled
}

/**
 * Get weld tolerance from environment variable or use default
 * @param {number} fallbackTolerance - Fallback tolerance if env var not set
 * @returns {number} - The weld tolerance to use
 */
function getWeldTolerance(fallbackTolerance = 1e-3) {
  const envTolerance = import.meta.env.VITE_MESH_WELD_TOLERANCE
  if (envTolerance !== undefined) {
    const parsed = parseFloat(envTolerance)
    if (!isNaN(parsed) && parsed > 0) {
      return parsed
    }
  }
  return fallbackTolerance
}

/**
 * Merge vertices with a tolerance, compute normals, and ensure indexed geometry
 * Optionally repairs topology issues like boundary edges and duplicate faces
 * @param {THREE.BufferGeometry} geometry - The geometry to process
 * @param {number} tolerance - Merge tolerance (default: read from env or 1e-4)
 * @param {string} source - Source identifier for logging (default: 'Unknown')
 * @param {Object} options - Additional options
 * @param {boolean} options.repairTopology - Whether to repair topology issues (default: read from env or true)
 * @param {number} options.weldTolerance - Tolerance for welding boundary edges (default: read from env or 1e-3)
 * @returns {THREE.BufferGeometry} - Processed geometry (indexed)
 */
export function mergeAndNormalizeGeometry(geometry, tolerance, source = 'Unknown', options = {}) {
  const {
    repairTopology,
    weldTolerance
  } = options

  // Use provided tolerance or get from environment
  const mergeTolerance = tolerance !== undefined ? tolerance : getMergeTolerance(1e-4)
  // Use provided repairTopology setting or get from environment/default
  const shouldRepair = repairTopology !== undefined ? repairTopology : isTopologyRepairEnabled()
  // Use provided weldTolerance or get from environment/default
  const finalWeldTolerance = weldTolerance !== undefined ? weldTolerance : getWeldTolerance(1e-3)

  // Log BEFORE merge
  const beforeVertices = geometry.attributes.position.count
  const beforeHasIndex = !!geometry.index
  const beforeTriangles = beforeHasIndex
    ? geometry.index.count / 3
    : beforeVertices / 3

  console.log(`🔍 BEFORE mergeVertices [${source}]:`, {
    vertices: beforeVertices,
    triangles: Math.floor(beforeTriangles),
    hasIndex: beforeHasIndex,
    indexCount: geometry.index?.count || 'N/A',
    tolerance: mergeTolerance
  })

  // Merge vertices with tolerance - this creates an indexed geometry
  geometry = BufferGeometryUtils.mergeVertices(geometry, mergeTolerance)

  // Log AFTER merge
  const afterVertices = geometry.attributes.position.count
  const afterHasIndex = !!geometry.index
  const afterTriangles = afterHasIndex
    ? geometry.index.count / 3
    : afterVertices / 3

  console.log(`✅ AFTER mergeVertices [${source}]:`, {
    vertices: afterVertices,
    triangles: Math.floor(afterTriangles),
    hasIndex: afterHasIndex,
    indexCount: geometry.index?.count || 'N/A',
    reduction: `${beforeVertices} → ${afterVertices} (${((1 - afterVertices/beforeVertices) * 100).toFixed(1)}% reduction)`
  })

  // Compute vertex normals on the indexed geometry
  // This ensures proper normal averaging across shared vertices
  geometry.computeVertexNormals()

  // Repair topology issues if enabled
  if (shouldRepair) {
    const verboseRepair = import.meta.env.VITE_MESH_REPAIR_VERBOSE === 'true'
    repairMeshTopology(geometry, {
      weldTolerance: finalWeldTolerance,
      removeDuplicates: true,
      verbose: verboseRepair
    })
  }

  // Validate mesh topology
  validateMeshTopology(geometry, source)

  // **CRITICAL**: Keep geometry INDEXED to preserve vertex topology
  // This maintains proper adjacency relationships for smoothening and other tools
  // mergeVertices() already returns indexed geometry, so we just return it

  return geometry
}

/**
 * Center and orient geometry with standard transformations
 * @param {THREE.BufferGeometry} geometry - The geometry to transform
 * @returns {THREE.BufferGeometry} - Transformed geometry
 */
export function centerAndOrientGeometry(geometry) {
  // Compute bounding box and center
  geometry.computeBoundingBox()
  const center = geometry.boundingBox.getCenter(new THREE.Vector3())

  // Translate to origin
  geometry.translate(-center.x, -center.y, -center.z)

  // Apply standard rotations
  geometry.rotateY(THREE.MathUtils.degToRad(STANDARD_ROTATION.y))
  geometry.rotateZ(THREE.MathUtils.degToRad(STANDARD_ROTATION.z))

  return geometry
}

/**
 * Setup color attribute for geometry
 * Handles:
 * - Existing color attributes
 * - Face colors that need to be expanded to vertex colors
 * - Default white color if no color exists
 * @param {THREE.BufferGeometry} geometry - The geometry to modify
 * @param {Object} options - Configuration options
 * @param {boolean} options.expandFaceColors - Whether to expand face colors to vertices (default: false)
 * @returns {THREE.BufferGeometry} - Geometry with color attribute
 */
export function setupColorAttribute(geometry, options = {}) {
  const { expandFaceColors = false } = options
  const count = geometry.attributes.position.count

  // If geometry already has color attribute
  if (geometry.hasAttribute('color')) {
    // Handle face colors that need to be expanded to vertex colors
    if (expandFaceColors) {
      const faceColors = geometry.getAttribute('color')
      if (faceColors.itemSize === 3 && faceColors.count !== count) {
        const vertexColors = new Float32Array(count * 3)
        const facesCount = Math.floor(count / 3)

        for (let i = 0; i < facesCount; i++) {
          const faceColor = [
            faceColors.getX(i),
            faceColors.getY(i),
            faceColors.getZ(i),
          ]

          // Assign same color to all 3 vertices of the triangle
          for (let v = 0; v < 3; v++) {
            const idx = (i * 3 + v) * 3
            vertexColors[idx + 0] = faceColor[0]
            vertexColors[idx + 1] = faceColor[1]
            vertexColors[idx + 2] = faceColor[2]
          }
        }

        geometry.setAttribute('color', new THREE.BufferAttribute(vertexColors, 3))
      }
    }
    // Color attribute already exists and is valid, no changes needed
    return geometry
  }

  // Add default white color
  const colors = new Float32Array(count * 3).fill(1)
  geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3))

  return geometry
}

/**
 * Setup weight attribute for geometry
 * Initializes weight attribute to 0 if not already present
 * @param {THREE.BufferGeometry} geometry - The geometry to modify
 * @returns {THREE.BufferGeometry} - Geometry with weight attribute
 */
export function setupWeightAttribute(geometry) {
  if (geometry.hasAttribute('weight')) {
    return geometry
  }

  const count = geometry.attributes.position.count
  const weights = new Float32Array(count)
  geometry.setAttribute('weight', new THREE.BufferAttribute(weights, 1))

  return geometry
}

/**
 * Create standard mesh material
 * @param {Object} overrides - Override specific material properties
 * @returns {THREE.MeshStandardMaterial} - Configured material
 */
export function createStandardMaterial(overrides = {}) {
  // return new THREE.MeshNormalMaterial({
  //   side: THREE.DoubleSide,
  // });
  return new THREE.MeshStandardMaterial({
    ...STANDARD_MATERIAL_CONFIG,
    ...overrides,
  })
}

/**
 * Create a mesh with geometry and material
 * @param {THREE.BufferGeometry} geometry - The geometry
 * @param {string} meshName - Name for the mesh
 * @param {THREE.Material} material - The material (optional, uses standard material if not provided)
 * @returns {THREE.Mesh} - Created mesh
 */
export function createMesh(geometry, meshName = 'Mesh', material = null) {
  const finalMaterial = material || createStandardMaterial()
  const mesh = new THREE.Mesh(geometry, finalMaterial)
  mesh.name = meshName
  // mesh.renderOrder = 1;
  return mesh
}

/**
 * Complete geometry processing pipeline
 * Combines all geometry processing steps
 * @param {THREE.BufferGeometry} geometry - The geometry to process
 * @param {Object} options - Configuration options
 * @param {number} options.mergeTolerance - Vertex merge tolerance (default: read from env or 1e-4)
 * @param {boolean} options.expandFaceColors - Expand face colors to vertices (default: false)
 * @param {boolean} options.repairTopology - Repair topology issues (default: true)
 * @param {number} options.weldTolerance - Tolerance for welding boundary edges (default: 1e-3)
 * @returns {THREE.BufferGeometry} - Fully processed geometry
 */
export function processGeometry(geometry, options = {}) {
  const {
    mergeTolerance,
    expandFaceColors = false,
    repairTopology = true,
    weldTolerance = 1e-3
  } = options

  // Center and orient
  centerAndOrientGeometry(geometry)

  // Merge and normalize with optional topology repair
  mergeAndNormalizeGeometry(geometry, mergeTolerance, 'ProcessGeometry', {
    repairTopology,
    weldTolerance
  })

  // Setup attributes
  setupColorAttribute(geometry, { expandFaceColors })
  setupWeightAttribute(geometry)

  return geometry
}

/**
 * Bake texture colors to vertex colors by sampling at UV coordinates
 * @param {THREE.BufferGeometry} geometry - The geometry with UV attributes
 * @param {THREE.Texture} texture - The texture to bake
 * @returns {THREE.BufferAttribute|null} - Vertex colors or null if unable to bake
 */
export function bakeTextureToVertexColors(geometry, texture) {
  if (!geometry.attributes.uv || !texture.image) {
    return null
  }

  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')

  canvas.width = texture.image.width
  canvas.height = texture.image.height
  ctx.drawImage(texture.image, 0, 0)

  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
  const pixels = imageData.data

  const uvs = geometry.attributes.uv
  const count = geometry.attributes.position.count
  const colors = new Float32Array(count * 3)

  for (let i = 0; i < count; i++) {
    const u = uvs.getX(i)
    const v = 1.0 - uvs.getY(i) // Flip V coordinate

    // Sample texture at UV coordinate
    const x = Math.floor(u * (canvas.width - 1))
    const y = Math.floor(v * (canvas.height - 1))
    const idx = (y * canvas.width + x) * 4

    colors[i * 3 + 0] = pixels[idx + 0] / 255 // R
    colors[i * 3 + 1] = pixels[idx + 1] / 255 // G
    colors[i * 3 + 2] = pixels[idx + 2] / 255 // B
  }

  return new THREE.BufferAttribute(colors, 3)
}
